import React, { useState, useMemo } from "react";
import { useQuery } from "@apollo/client";
import { useLocation, useParams } from "react-router-dom";
import { Box, Typography, IconButton, Tooltip } from "@mui/material";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import { CheckStatusChip } from "../../../../components/CheckStatusChip/CheckStatusChip";
import { GET_EVIDENCE_BY_CHECK_ID, GET_COMMON_EXCEPTIONS } from "../../../../apis/settingsApis/framework-apis/frameworkApis";
import { ExceptionScope } from "../../../../utils/enums";
import EvidenceTableContainer from "../common/EvidenceTableContainer";
import ReusableTable from "../../../../components/ReusableTable/ReusableTable";
import EvidenceLoadingSkeleton from "../common/EvidenceLoadingSkeleton";
import FilePreviewDialog from "./FilePreviewDialog";
import EntityDetailDialog from "../../../IntegratedEvidence/EntityDetailDialog";
import { formatDate } from "../../../../utils/dateUtils";

const isFileEvidence = (row) =>
  row.entityType === "file" && (!!row.path || row.fileId != null);

const isViewableLinkedEvidence = (row) =>
  !!row.entityId && row.entityType !== "file";

const isDisplayableEvidence = (row) =>
  isFileEvidence(row) || isViewableLinkedEvidence(row);

const PassWithEvidenceTab = ({ checkDetails, activeCheckId, onRefetch, auditEndDate }) => {
  const location = useLocation();
  const { auditId: paramAuditId } = useParams();
  const isAuditorView = location.pathname.startsWith("/auditor/");
  const auditId = location.state?.audit?.id || paramAuditId;
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedFileException, setSelectedFileException] = useState(null);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedEntity, setSelectedEntity] = useState({ id: null, type: null });

  // Fetch files for this check
  const { data: filesData, loading: filesLoading } = useQuery(GET_EVIDENCE_BY_CHECK_ID, {
    variables: {
      check_id: Number(activeCheckId),
      audit_id: auditId,
      is_valid: true,
    },
    skip: !activeCheckId || (isAuditorView && !auditId),
    fetchPolicy: "network-only",
  });

  // Fetch exceptions for this check
  const { data: exceptionsData, loading: exceptionsLoading } = useQuery(GET_COMMON_EXCEPTIONS, {
    variables: {
      filters: {
        active_check_id: activeCheckId,
        scope: ExceptionScope.GLOBAL,
      },
      audit_id: auditId,
      page: 1,
      limit: 100,
    },
    skip: !activeCheckId || (isAuditorView && !auditId),
    fetchPolicy: "network-only",
  });

  const evidenceItems = useMemo(
    () =>
      (filesData?.getEvidenceByCheckId || []).filter(isDisplayableEvidence),
    [filesData]
  );

  const exceptions = useMemo(() => {
    const allExceptions = exceptionsData?.getCommonExceptions?.data || [];

    // If no audit end date, return all exceptions
    if (!auditEndDate) return allExceptions;

    const auditEndDateTime = new Date(auditEndDate).getTime();

    // Filter exceptions valid during the audit period
    return allExceptions.filter((exc) => {
      if (exc.created_at) {
        const createdAt = new Date(exc.created_at).getTime();
        if (createdAt > auditEndDateTime) return false;
      }

      // Exception must be valid through audit end date (or indefinitely)
      if (exc.exception_valid_till) {
        const validTill = new Date(exc.exception_valid_till).getTime();
        if (validTill < auditEndDateTime) return false;
      }

      return true;
    });
  }, [exceptionsData, auditEndDate]);

  // Match evidence with exceptions (files, linked entities, or legacy file_id)
  const evidenceWithExceptions = useMemo(() => {
    return evidenceItems.map((evidence) => {
      const exception = exceptions.find((exc) => {
        if (exc.file_id != null && exc.file_id === evidence.fileId) return true;
        if (
          exc.entity_type === "file" &&
          exc.entity_id != null &&
          String(exc.entity_id) === String(evidence.fileId)
        ) {
          return true;
        }
        if (
          exc.entity_type &&
          exc.entity_type !== "file" &&
          exc.entity_id != null &&
          evidence.entityId != null &&
          String(exc.entity_type) === String(evidence.entityType) &&
          String(exc.entity_id) === String(evidence.entityId)
        ) {
          return true;
        }
        return false;
      });
      return {
        ...evidence,
        exception,
      };
    });
  }, [evidenceItems, exceptions]);

  const checkLevelException = useMemo(() => {
    return exceptions.find(
      (exc) => exc.file_id == null && exc.entity_type !== "file"
    );
  }, [exceptions]);

  // Search filtering
  const searchedEvidence = useMemo(() => {
    if (!searchTerm) return evidenceWithExceptions;

    const query = searchTerm.toLowerCase();
    return evidenceWithExceptions.filter((evidence) =>
      (evidence.maskName || evidence.entityName || "")
        .toLowerCase()
        .includes(query)
    );
  }, [evidenceWithExceptions, searchTerm]);

  // Fallback row shown when no file evidence exists but a check-level exception does.
  const showExceptionFallback =
    searchedEvidence.length === 0 && !searchTerm && checkLevelException;

  const tableRows = useMemo(() => {
    if (showExceptionFallback) {
      return [
        {
          __isExceptionFallback: true,
          exception: checkLevelException,
        },
      ];
    }
    return searchedEvidence;
  }, [showExceptionFallback, checkLevelException, searchedEvidence]);

  const handleViewEvidence = (evidence) => {
    if (isFileEvidence(evidence)) {
      setSelectedFile(evidence);
      setSelectedFileException(evidence.exception);
      setPreviewDialogOpen(true);
      return;
    }

    if (isViewableLinkedEvidence(evidence)) {
      setSelectedEntity({ id: evidence.entityId, type: evidence.entityType });
      setDetailOpen(true);
    }
  };

  const columns = useMemo(
    () => [
      {
        label: showExceptionFallback ? "Check Status" : "Evidence Name",
        field: "maskName",
        align: "left",
        width: "35%",
      },
      { label: "Justification", field: "justification", align: "left", width: "35%" },
      { label: "Valid Until", field: "validUntil", align: "left", width: "25%" },
      { label: "", field: "actions", align: "right", width: "5%" },
    ],
    [showExceptionFallback]
  );


  const renderCell = (row, column) => {
    if (row.__isExceptionFallback) {
      switch (column.field) {
        case "maskName":
          return (
            <Box sx={{ display: "inline-flex" }}>
              <CheckStatusChip status={checkDetails?.status} count={0} />
            </Box>
          );
        case "justification":
          return (
            <Tooltip title={row.exception?.reason || "—"} arrow placement="top">
              <Typography
                variant="body2"
                sx={{
                  color: "text.secondary",
                  fontSize: "14px",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {row.exception?.reason || "—"}
              </Typography>
            </Tooltip>
          );
        case "validUntil":
          return (
            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
                fontSize: "14px",
                whiteSpace: "nowrap",
              }}
            >
              {row.exception?.exception_valid_till
                ? `Until ${formatDate(row.exception.exception_valid_till)}`
                : "—"}
            </Typography>
          );
        case "actions":
          return null;
        default:
          return null;
      }
    }

    switch (column.field) {
      case "maskName":
        return (
          <Typography
            variant="body2"
            sx={{
              fontWeight: 500,
              fontSize: "0.8125rem",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {row.maskName || row.entityName || "Unknown Evidence"}
          </Typography>
        );

      case "justification":
        return (
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
              fontSize: "14px",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {row.exception?.reason || "N/A"}
          </Typography>
        );

      case "validUntil":
        return (
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
              fontSize: "14px",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {row.exception?.exception_valid_till
              ? formatDate(row.exception.exception_valid_till)
              : "N/A"}
          </Typography>
        );

      case "actions":
        if (!isFileEvidence(row) && !isViewableLinkedEvidence(row)) {
          return null;
        }
        return (
          <Tooltip
            title={
              isFileEvidence(row)
                ? "View file"
                : `View ${row.entityType || "evidence"}`
            }
          >
            <IconButton
              size="small"
              onClick={() => handleViewEvidence(row)}
              color="primary"
            >
              <RemoveRedEyeIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        );

      default:
        return (
          <Typography
            variant="body2"
            sx={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {row[column.field]}
          </Typography>
        );
    }
  };

  const loading = filesLoading || exceptionsLoading;

  if (loading) {
    return <EvidenceLoadingSkeleton />;
  }

  return (
    <>
      <EvidenceTableContainer
        title="Resources"
        searchTerm={searchTerm}
        onSearchChange={(e) => setSearchTerm(e.target.value)}
        clearFilters={() => setSearchTerm("")}
        filteredCount={tableRows.length}
        countLabel={`${tableRows.length} ${tableRows.length === 1 ? "resource" : "resources"
          } found`}
      >
        <ReusableTable
          columns={columns}
          data={tableRows}
          renderCell={renderCell}
          height="auto"
          showPagination={true}
          rowsPerPageOptions={[10, 25, 50]}
          emptyMessage="No evidence uploaded for this check"
        />
      </EvidenceTableContainer>

      <FilePreviewDialog
        open={previewDialogOpen}
        onClose={() => setPreviewDialogOpen(false)}
        file={selectedFile}
        exception={selectedFileException}
      />

      <EntityDetailDialog
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        entityId={selectedEntity.id}
        entityType={selectedEntity.type}
        contextCheckId={activeCheckId}
      />
    </>
  );
};

export default PassWithEvidenceTab;
